import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Database, Plus, Search, Warehouse, Package, AlertTriangle, CheckCircle2, Sliders, Edit, X, RefreshCw } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function StockPage() {
  const [search, setSearch] = useState('')
  const [editingItem, setEditingItem] = useState<any | null>(null)
  const [newQuantity, setNewQuantity] = useState('')

  const queryClient = useQueryClient()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['/inventory/stock', { search }],
    queryFn: () => api.get('/inventory/stock', { params: { search } }).then(r => r.data),
  })

  const updateMutation = useMutation({
    mutationFn: ({ product_id, quantity }: { product_id: string; quantity: number }) =>
      api.patch(`/products/${product_id}`, { stock_quantity: quantity, stock: quantity }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/inventory/stock'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('موجودی کالا به‌روزرسانی شد')
      setEditingItem(null)
    },
    onError: () => toast.error('خطا در اصلاح موجودی')
  })

  const items = Array.isArray(data) ? data : (data?.items || [
    {
      product_id: 'prod_1',
      product_name: 'گوشی موبایل سامسونگ Galaxy A55',
      warehouse_name: 'انبار مرکزی تهران',
      quantity: 15,
      min_level: 5,
      reorder_point: 8
    },
    {
      product_id: 'prod_2',
      product_name: 'لپ‌تاپ ایسوس Vivobook 15',
      warehouse_name: 'انبار مرکزی تهران',
      quantity: 3,
      min_level: 5,
      reorder_point: 5
    },
    {
      product_id: 'prod_3',
      product_name: 'هندزفری بادی فنری هایلو',
      warehouse_name: 'انبار خاوران',
      quantity: 0,
      min_level: 10,
      reorder_point: 15
    }
  ])

  const filteredItems = items.filter((item: any) => {
    if (!search) return true
    return item.product_name?.toLowerCase().includes(search.toLowerCase()) ||
           item.warehouse_name?.toLowerCase().includes(search.toLowerCase())
  })

  const lowStockCount = items.filter((i: any) => i.quantity > 0 && i.quantity <= (i.min_level || 5)).length
  const outOfStockCount = items.filter((i: any) => i.quantity <= 0).length

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-primary-500" />
            موجودی و نقطه سفارش کالاها
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            پایش لحظه‌ای تعداد کالاها در کلیه انبارها و بررسی هشدارهای کسری موجودی
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          به‌روزرسانی جدول
        </Button>
      </motion.div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">کل اقلام انبار</p>
            <p className="text-lg font-bold text-neutral-900 dark:text-white mt-0.5">{items.length} کالا</p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4 border-r-4 border-r-amber-500">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">هشدار کمبود موجودی</p>
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400 mt-0.5">{lowStockCount} کالا</p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4 border-r-4 border-r-red-500">
          <div className="w-12 h-12 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center">
            <X className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 font-medium">ناموجود در انبار</p>
            <p className="text-lg font-bold text-red-600 dark:text-red-400 mt-0.5">{outOfStockCount} کالا</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="جستجوی نام کالا یا انبار..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-6 w-48" /></div>)}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="card p-12 text-center">
          <Database className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">موجودی کالا یافت نشد</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">نام کالا</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">انبار نگهداری</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">موجودی فعلی</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">حداقل موجودی</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">وضعیت</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500">اصلاح موجودی</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {filteredItems.map((item: any) => {
                  const q = item.quantity ?? 0
                  const min = item.min_level ?? 5
                  const isOut = q <= 0
                  const isLow = q > 0 && q <= min

                  return (
                    <tr key={item.product_id || item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                      <td className="px-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary-500" />
                        {item.product_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                        <div className="flex items-center gap-1.5">
                          <Warehouse className="w-3.5 h-3.5 text-neutral-400" />
                          {item.warehouse_name || 'انبار اصلی'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-neutral-900 dark:text-white">
                        {q} عدد
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {min} عدد
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isOut ? (
                          <Badge variant="danger" className="gap-1">
                            <X className="w-3 h-3" /> ناموجود
                          </Badge>
                        ) : isLow ? (
                          <Badge variant="warning" className="gap-1">
                            <AlertTriangle className="w-3 h-3" /> کمبود موجودی
                          </Badge>
                        ) : (
                          <Badge variant="success" className="gap-1">
                            <CheckCircle2 className="w-3 h-3" /> کافی
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-left">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingItem(item)
                            setNewQuantity(String(q))
                          }}
                          className="gap-1 text-xs"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          تنظیم موجودی
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Quick Adjust Stock */}
      <AnimatePresence>
        {editingItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Sliders className="w-5 h-5 text-primary-500" />
                  اصلاح موجودی {editingItem.product_name}
                </h3>
                <button onClick={() => setEditingItem(null)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  updateMutation.mutate({
                    product_id: editingItem.product_id,
                    quantity: Number(newQuantity) || 0
                  })
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    تعداد موجودی جدید (عدد)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={newQuantity}
                    onChange={(e) => setNewQuantity(e.target.value)}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setEditingItem(null)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={updateMutation.isPending}>
                    ذخیره تغییرات
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
